import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { CustomValidators } from 'src/app/etc/form-validators';
import { IEvent } from 'src/app/models/event.model';
import { IPromoCode } from 'src/app/models/promo-code.model';
import { ITicketType } from 'src/app/models/ticket.model';
import { DateInterval, GetParams } from 'src/app/models/type.definition';
import { CurrencyService, PromoCodeService, TicketTypeService } from 'src/app/providers';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { NotificationService } from 'src/app/services/notification.service';
import { UpdateService } from 'src/app/services/update.service';
import { GetEvent } from '../../../getEvent';

@Component({
  selector: 'app-event-ticketing-update-promo-code',
  templateUrl: './event-ticketing-update-promo-code.component.html',
  styleUrls: ['./event-ticketing-update-promo-code.component.scss'],
  providers: [GetEvent]
})

export class EventTicketingUpdatePromoCodeComponent implements OnInit {
  private _subscription = new Subscription();
  public event: IEvent;
  public error: string;
  public loadingState$: Subject<void> = new Subject<void>();
  public currentUrl;
  public checkedTicketTypesIds: string[] = [];
  public promoCode: IPromoCode;
  public promoForm: FormGroup;
  public discount: string;
  public currencySymbol: string;
  public restrictTicket: boolean;
  public restrictDate: boolean;
  public selectedTickets: any[];
  public ticketsType: ITicketType[] = [];
  public dropDown = false;
  public checkedTicket: { _id: ITicketType, amount: number }[] = [];

  public dates: { startDate: Date, endDate: Date } = { startDate: null, endDate: null };

  constructor(
    private formBuilder: FormBuilder,
    private getEvent: GetEvent,
    private router: Router,
    private promoCodeService: PromoCodeService,
    private notification: NotificationService,
    private errorHandler: ErrorHandlerService,
    private currencyService: CurrencyService,
    private update: UpdateService,
    private ticketTypeService: TicketTypeService,
    private activatedRoute: ActivatedRoute
  ) { }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  ngOnInit() {
    const id = this.activatedRoute.snapshot.paramMap.get('promoCodeId');
    this._subscription.add(this.promoCodeService.getById(id).subscribe(async res => {
      if(res){
        this.promoCode = res;
        this.checkedTicketTypesIds = this.promoCode.constraintTicketTypesIds.map(_ => _._id) || [];

        if(this.promoCode.reduction.type === 'free'){
          this.promoCode.reduction.amount = null;
        }
        this.discount = this.promoCode.reduction.type;
        this.currentUrl = this.router.url;
        const eventBody: GetParams<IEvent> = {
          select: ['name', 'ticketing']
        }
        this.event = await this.getEvent.get(eventBody);
        const ticketTypeBody: GetParams<ITicketType> = {
          filter: {
            eventId: this.event._id,
            isProduct: false,
          },
          select: ['name', 'ticketTypeCategoryId'],
          populate: ['ticketTypeCategoryId'],
          perPage: 500
        };
        this.currencySymbol = this.event.ticketing.defaultCurrencyId.symbol;
        this.changeDiscount(this.promoCode.reduction.type);
        this.restrictTicket = this.promoCode.constraintTicketTypesIds !== undefined && !!this.promoCode.constraintTicketTypesIds.length;
        this.restrictDate = !!this.promoCode.constraintBeginDate || !!this.promoCode.constraintEndDate
        if (this.restrictDate) {
          this.dates = { startDate: this.promoCode.constraintBeginDate, endDate: this.promoCode.constraintEndDate }
        }
        this._subscription.add(this.ticketTypeService.getList(ticketTypeBody).subscribe(res => {
          this.ticketsType = res?.data || [];
          this.initForm();
        }))
        this.checkedTicket = this.promoCode.gridPrice?.map(x => {
          return {
            _id: x.ticketTypeId,
            amount: x.amount
          }
        })
      }
      else {
        this.goBack();
      }
    }))
  }

  initForm() {
    this.promoForm = this.formBuilder.group({
      name: new FormControl(this.promoCode.name, Validators.required),
      code: new FormControl(this.promoCode.code, Validators.required),
      counterScope: new FormControl(this.promoCode.counterScope, Validators.required),
      reduction: this.formBuilder.group({
        type: new FormControl(this.promoCode.reduction.type, Validators.required),
        amount: new FormControl(this.promoCode.reduction.amount, CustomValidators.numberIsPositive)
      }),
      constraintTicketTypesIds: new FormControl(this.promoCode.constraintTicketTypesIds),
      constraintBeginDate: new FormControl(this.promoCode.constraintBeginDate),
      constraintEndDate: new FormControl(this.promoCode.constraintEndDate),
      constraintQuantity: new FormControl(this.promoCode.constraintQuantity, [Validators.required, CustomValidators.numberIsPositiveInteger])
    })
  }

  updatePromoCode() {
    const formData = {
      eventId: this.event._id,
      status: 'active',
      ...this.promoForm.value
    }
    if (formData.reduction.type === 'free') {
      formData.reduction.amount = 1;
    }
    formData.constraintTicketTypesIds = this.checkedTicketTypesIds;

    if (this.discount === 'grid_price') {
      formData.gridPrice = this.checkedTicket.map(x => {
        return {
          ticketTypeId: x._id,
          amount: x.amount || 0
        }
      })
      delete formData.reduction.amount
    }

    this.promoCodeService.update(this.promoCode._id, formData).subscribe(result => {
      this.update.object(this.promoCode, formData);
      this.loadingState$.next();
      this.notification.newNotification({
        message: 'Code promo créé avec succès',
        state: 'success'
      })
      this.goBack();
    },
      (err: any) => {
        this.error = this.errorHandler.getError(err)
        this.loadingState$.next();
      });
  }

  noAmount(): boolean {
    const amount = this.promoForm.value.reduction.amount;
    const gridAmount = this.checkedTicket?.some(x => x.amount === 0)
    return this.discount === 'grid_price' ? gridAmount : (!amount && this.discount !== 'free');
  }

  noDate(): boolean {
    return this.restrictDate && (!this.dates.startDate || !this.dates.endDate);
  }

  changeDiscount(value) {
    switch (value) {
      case 'percentage':
        this.discount = '%';
        break;
      case 'flat':
        this.discount = this.currencySymbol;
        break;
      case 'free':
        this.discount = 'free';
        break
    }
  }

  goBack(): void {
    this.router.navigate([`evenements/${this.event._id}/billetterie/codes-promos`])
  }

  displayRestrictedTicket(): void {
    this.restrictTicket = !this.restrictTicket;
  }

  displayRestrictedDate(): void {
    this.restrictDate = !this.restrictDate;
  }

  changeDates(dates) {
    const beginDate = dates.startDate;
    const endDate = dates.endDate;
    this.dates = dates;
    this.promoForm.patchValue({ constraintBeginDate: beginDate, constraintEndDate: endDate });
  }

  changeCounterScope(scope) {
    this.promoForm.get('counterScope').patchValue(scope)
  }

  public checkTicket(id: string, value: boolean, ticket: any): void {
    const inArray = this.checkedTicketTypesIds.includes(id);
    if (value && !inArray) {
      this.checkedTicketTypesIds.push(id);
      this.checkedTicket.push(ticket)
    }
    else if (!value && inArray) {
      this.checkedTicketTypesIds = this.checkedTicketTypesIds.filter(_ => _ !== id);
      this.checkedTicket = this.checkedTicket.filter(t => t._id !== id);
    }
  }

  public ticketChecked(id: string): boolean {
    return this.checkedTicketTypesIds.includes(id) || null;
  }

  public getTicketsName(): string {
    const names = this.ticketsType.filter(_ => this.checkedTicketTypesIds.includes(_._id));
    if (!names.length) {
      return 'Séléctionner des tickets';
    }
    else {
      const fullString = names.map(_ => _.name).join(', ');
      return fullString.length < 80 ? fullString : `${fullString.substring(0, 77)} ...`
    }
  }

  @HostListener('document:click') clickDoc(): void {
    this.dropDown = false;
  }
}
